import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useGetIdentity, useSelect } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import L from "leaflet";

import { apiClient } from "../../services/api/apiClient";
import { PageHeader } from "../../components/common/PageHeader";
import { PlotMapEditor } from "../audits/components/PlotMapEditor";
import { ADMIN_USER_TYPES, isEurosemillasScoped } from "../../types/auth";
import type { UserInfo } from "../../types/auth";

const { Text } = Typography;

/** Área geodésica en hectáreas para un GeoJSON Polygon o MultiPolygon.
 *  Usa `L.GeometryUtil.geodesicArea` (leaflet-draw) — mismo patrón que
 *  se usa en pages/adjacent-crops/create.tsx. Suficientemente exacta
 *  como hint para el input editable de superficie. Devuelve null si
 *  la geometría no es un polígono válido o si leaflet-draw no ha
 *  cargado (SSR/edge cases). */
const polygonAreaHa = (geom: GeoJSON.Geometry | null): number | null => {
  if (!geom) return null;
  const util = (L as any).GeometryUtil;
  if (!util?.geodesicArea) return null;
  const ringToLatLngs = (ring: number[][]) =>
    ring.map(([lng, lat]) => L.latLng(lat, lng));
  try {
    if (geom.type === "Polygon") {
      const ring = (geom as GeoJSON.Polygon).coordinates[0];
      const m2 = util.geodesicArea(ringToLatLngs(ring));
      return m2 / 10000;
    }
    if (geom.type === "MultiPolygon") {
      const total = (geom as GeoJSON.MultiPolygon).coordinates.reduce(
        (acc, poly) => acc + util.geodesicArea(ringToLatLngs(poly[0])),
        0,
      );
      return total / 10000;
    }
  } catch {
    return null;
  }
  return null;
};

/**
 * Admin-only editor for a canonical PlotReal.
 *
 * Route: `/farms/:farmId/plot-reals/:id/edit` (defined in App.tsx under
 * `<AdminRoute>` — non-admins never see this page).
 *
 * Payload maps 1:1 to `PlotRealAdminUpdateSerializer` on the backend.
 * The PATCH endpoint computes a diff against the current row and
 * creates a `PlotRealRevision` with `change_summary='admin-edit'`, so
 * every field the admin touches ends up in the farm's history panel.
 */

interface PlotRealDetail {
  id: number;
  name: string | null;
  farm: {
    id: number;
    name: string | null;
    producer_name: string | null;
  } | null;
  rootstock_id: number | null;
  rootstock_name: string | null;
  intermediate_wood_id: number | null;
  intermediate_wood_name: string | null;
  crop_date: number | null;
  plantation_frame_row: number | null;
  plantation_frame_column: number | null;
  plants: number | null;
  grafting_type: number | null;
  grafting_type_label: string | null;
  grafting: number | null;
  surface: number | null;
  status: string;
  has_geom: boolean;
  geom_geojson: GeoJSON.Geometry | null;
}

// Del modelo Django (apps/plots/models/plot_real.py:20-28) — no lo
// exponemos por API porque las choices no cambian.
const GRAFTING_TYPE_OPTIONS = [
  { value: 1, label: "Plancha" },
  { value: 2, label: "Yema" },
  { value: 3, label: "Escudo" },
  { value: 4, label: "Púas" },
  { value: 5, label: "N/A" },
];

export const PlotRealEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { farmId, id } = useParams<{ farmId: string; id: string }>();
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin =
    (ADMIN_USER_TYPES as readonly number[]).includes(
      identity?.app_user?.user_type ?? -1,
    ) && !isEurosemillasScoped(identity);

  const [form] = Form.useForm();
  const [detail, setDetail] = useState<PlotRealDetail | null>(null);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Estos 3 valores del form los "vigilamos" para poder pintar debajo
  // los cálculos derivados (superficie del polígono, plantas según
  // marco). `Form.useWatch` re-renderiza el componente cuando cambian.
  const surfaceValue = Form.useWatch("surface", form) as number | undefined;
  const frameRowValue = Form.useWatch("plantation_frame_row", form) as
    | number
    | undefined;
  const frameColValue = Form.useWatch("plantation_frame_column", form) as
    | number
    | undefined;

  // Hectáreas calculadas a partir del polígono actualmente dibujado.
  const polygonHa = useMemo(() => polygonAreaHa(geometry), [geometry]);

  // Plantas estimadas = superficie (ha) × 10.000 / (marco fila × marco col).
  // Redondeamos al entero más cercano. Solo hay valor cuando las 3
  // entradas son > 0 (evitamos division-by-zero y hints sin sentido).
  const estimatedPlants = useMemo(() => {
    if (!surfaceValue || !frameRowValue || !frameColValue) return null;
    if (surfaceValue <= 0 || frameRowValue <= 0 || frameColValue <= 0)
      return null;
    return Math.round(
      (surfaceValue * 10000) / (frameRowValue * frameColValue),
    );
  }, [surfaceValue, frameRowValue, frameColValue]);

  const applyPolygonSurface = () => {
    if (polygonHa == null) return;
    form.setFieldsValue({ surface: Number(polygonHa.toFixed(2)) });
  };
  const applyEstimatedPlants = () => {
    if (estimatedPlants == null) return;
    form.setFieldsValue({ plants: estimatedPlants });
  };

  // Selects catalog — mismo patrón que plot-form.tsx.
  const { options: patternOptions } = useSelect({
    resource: "patterns",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
  });
  const { options: intermediateWoodOptions } = useSelect({
    resource: "intermediate-woods",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
  });

  // Fetch detail on mount / id change.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const r = await apiClient.get<PlotRealDetail>(
          `/plot-reals/${id}/detail/?include_geom=1`,
        );
        if (cancelled) return;
        setDetail(r.data);
        setGeometry(r.data.geom_geojson);
        form.setFieldsValue({
          name: r.data.name ?? "",
          rootstock_id: r.data.rootstock_id ?? undefined,
          intermediate_wood_id: r.data.intermediate_wood_id ?? undefined,
          crop_date: r.data.crop_date ?? undefined,
          plantation_frame_row: r.data.plantation_frame_row ?? undefined,
          plantation_frame_column: r.data.plantation_frame_column ?? undefined,
          plants: r.data.plants ?? undefined,
          grafting_type: r.data.grafting_type ?? 5,
          grafting: r.data.grafting ?? 0,
          surface: r.data.surface ?? undefined,
        });
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(
            e?.response?.data?.detail ??
              e?.message ??
              "No se pudo cargar la parcela.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, form]);

  const backToFarm = () => navigate(`/farms/show/${farmId}`);

  const handleSave = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!id) return;
    setSaving(true);
    try {
      // Payload: mandamos solo los que tienen valor definido. `geom`
      // se manda siempre (así el usuario puede vaciarla si limpia el
      // polígono en el mapa).
      const payload: Record<string, any> = { ...values, geom: geometry };
      const r = await apiClient.patch<{
        noop: boolean;
        diff: Record<string, unknown>;
      }>(`/plot-reals/${id}/`, payload);
      if (r.data.noop) {
        message.info(
          t(
            "farms.plot_real_edit.noop",
            "Sin cambios que guardar.",
          ),
        );
      } else {
        message.success(
          t(
            "farms.plot_real_edit.saved",
            "Parcela actualizada. Los cambios quedan en el historial.",
          ),
        );
      }
      backToFarm();
    } catch (e: any) {
      const detail = e?.response?.data;
      const errText =
        typeof detail === "string"
          ? detail
          : detail?.detail ??
            Object.entries(detail || {})
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
              .join(" · ") ??
            e?.message ??
            "Error al guardar la parcela.";
      message.error(errText);
    } finally {
      setSaving(false);
    }
  };

  const yearOptions = useMemo(() => {
    const cur = new Date().getFullYear() + 5;
    const opts: { value: number; label: number }[] = [];
    for (let y = 2010; y <= cur; y++) opts.push({ value: y, label: y });
    return opts;
  }, []);

  if (!isAdmin) {
    return (
      <Card>
        <Alert
          type="error"
          message={t(
            "common.forbidden",
            "No tienes permiso para acceder a esta página.",
          )}
        />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <Spin size="large" />
      </Card>
    );
  }
  if (loadError || !detail) {
    return (
      <Card>
        <Alert type="error" message={loadError ?? "No se pudo cargar la parcela."} />
        <Button onClick={backToFarm} style={{ marginTop: 12 }}>
          {t("common.back", "Volver")}
        </Button>
      </Card>
    );
  }

  return (
    <Card>
      {/* Botón Volver como fila propia arriba, no dentro del title del
          PageHeader (donde antes quedaba minúsculo/mezclado con el
          h4). Con default type + icon queda como un pill claro. */}
      <div style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={backToFarm}>
          {t("common.back", "Volver")}
        </Button>
      </div>

      <PageHeader
        title={t(
          "farms.plot_real_edit.title",
          "Editar parcela canónica {{name}}",
          { name: detail.name || `#${detail.id}` },
        )}
        subtitle={
          <Text type="secondary">
            {detail.farm?.name}
            {detail.farm?.producer_name && ` · ${detail.farm.producer_name}`}
            {detail.status === "closed" && (
              <Text type="warning" style={{ marginLeft: 12 }}>
                {t(
                  "farms.plot_real_edit.closed_warning",
                  "Esta parcela está cerrada. Editar la reabre implícitamente al historial.",
                )}
              </Text>
            )}
          </Text>
        }
      />

      {/* Sin `onFinish` — el guardado se dispara solo desde el botón
          "Guardar" (click). Antes, pulsar Enter en cualquier input
          dentro del formulario (por ejemplo el buscador lat/long del
          mapa) hacía submit implícito → handleSave → navigate al
          detalle de la finca, sacando al usuario del edit sin querer. */}
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 8 }}
      >
        <Form.Item
          label={t("plot_real.name", "Nombre")}
          name="name"
          rules={[{ max: 100 }]}
        >
          <Input placeholder="Nombre de la parcela" />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("plot_real.crop_date", "Año de plantación")}
              name="crop_date"
            >
              <Select
                options={yearOptions}
                placeholder="Año"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("plot_real.rootstock", "Patrón")}
              name="rootstock_id"
            >
              <Select
                options={patternOptions}
                placeholder="Patrón"
                showSearch
                allowClear
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("plot_real.surface", "Superficie (ha)")}
              name="surface"
              // Reservamos el hint bajo el input con `extra` — así
              // el mensaje de "polígono = X.XX ha · Usar" queda
              // alineado y no rompe el marco del form.
              extra={
                polygonHa != null ? (
                  <Space size={6} style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t(
                        "plot_real.polygon_area_hint",
                        "Polígono del mapa: {{ha}} ha",
                        { ha: polygonHa.toFixed(2) },
                      )}
                    </Text>
                    <Button
                      size="small"
                      type="link"
                      onClick={applyPolygonSurface}
                      style={{ padding: 0, height: "auto" }}
                    >
                      {t("plot_real.use_polygon_area", "Usar este valor")}
                    </Button>
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t(
                      "plot_real.polygon_area_empty",
                      "Dibuja el polígono en el mapa para calcular la superficie.",
                    )}
                  </Text>
                )
              }
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                addonAfter="ha"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("plot_real.plants", "Número de plantas")}
              name="plants"
              extra={
                estimatedPlants != null ? (
                  <Space size={6} style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t(
                        "plot_real.plants_estimate_hint",
                        "Según superficie × marco: {{n}} plantas",
                        { n: estimatedPlants },
                      )}
                    </Text>
                    <Button
                      size="small"
                      type="link"
                      onClick={applyEstimatedPlants}
                      style={{ padding: 0, height: "auto" }}
                    >
                      {t("plot_real.use_plants_estimate", "Usar este valor")}
                    </Button>
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t(
                      "plot_real.plants_estimate_empty",
                      "Rellena superficie y marco para calcular las plantas.",
                    )}
                  </Text>
                )
              }
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("plot_real.frame_row", "Marco fila (m)")}
              name="plantation_frame_row"
            >
              <InputNumber
                min={0}
                step={0.1}
                style={{ width: "100%" }}
                addonAfter="m"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("plot_real.frame_column", "Marco columna (m)")}
              name="plantation_frame_column"
            >
              <InputNumber
                min={0}
                step={0.1}
                style={{ width: "100%" }}
                addonAfter="m"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t("plot_real.intermediate_wood", "Madera intermedia")}
          name="intermediate_wood_id"
        >
          <Select
            options={intermediateWoodOptions}
            placeholder="Madera intermedia"
            showSearch
            allowClear
            optionFilterProp="label"
          />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("plot_real.grafting_type", "Tipo de injerto")}
              name="grafting_type"
            >
              <Select options={GRAFTING_TYPE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("plot_real.grafting", "Número de injertos")}
              name="grafting"
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        {/* Mapa a ancho completo debajo de los inputs — así se
            aprovecha el espacio horizontal y el auditor/admin ve la
            geometría con detalle sin scroll lateral. */}
        <Card
          size="small"
          title={t("plot_real.geometry", "Geometría de la parcela")}
          styles={{ body: { padding: 0, height: 640 } }}
          style={{ marginTop: 8, marginBottom: 16 }}
        >
          <PlotMapEditor
            geometry={geometry}
            plotName={detail.name || `#${detail.id}`}
            editable
            onGeometryChange={setGeometry}
          />
        </Card>

        <Space>
          <Button onClick={backToFarm}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      </Form>
    </Card>
  );
};

export default PlotRealEdit;
